import { Row, Col, Card, Space, Select, Progress, Typography, Spin, Alert } from 'antd';
import { Pie, Line } from '@ant-design/plots';
import { WalletOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { formatMoney, formatNumber } from '../utils';
import { useDashboardAnalytics, useDashboardAds } from '../hooks/useDashboardQueries';

const { Text } = Typography;

const colorMap: Record<string, string> = {
    'COMMISSION': '#1890ff',
    'RADIUS': '#52c41a',
    'USER_SEAT': '#fa8c16',
    'SPONSORED_AD': '#eb2f96',
    'OTHER': '#722ed1',
};

export const AnalyticsSection = ({
    trendTimeframe,
    setTrendTimeframe,
    dateRange,
    compareDateRange,
    isComparing,
    compareLabel
}: any) => {
    const navigate = useNavigate();

    const params = {
        startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
        timeframe: trendTimeframe,
        timezone: 'Asia/Kolkata',
    };

    const { data: analyticsRes, isLoading: loadingAnalytics, isError: errorAnalytics } = useDashboardAnalytics(params);
    const { data: adsRes, isLoading: loadingAds, isError: errorAds } = useDashboardAds(params);

    if (loadingAnalytics || loadingAds) return <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>;
    if (errorAnalytics || errorAds) return <Alert type="error" message="Failed to load analytics data" style={{ marginBottom: 16 }} />;

    const analyticsData = analyticsRes?.data;
    const adsData = adsRes?.data;

    const breakdown = (analyticsData?.grossRevenueBreakdown || []).map(item => ({
        type: item.type,
        value: item.amount.amount,
        color: colorMap[item.type] || '#bfbfbf'
    }));
    const totalGross = analyticsData?.grossRevenue?.amount || 1; // avoid div by 0

    const pieConfig = {
        data: breakdown,
        angleField: 'value',
        colorField: 'type',
        radius: 1,
        innerRadius: 0.64,
        label: false,
        legend: false,
        color: breakdown.map(d => d.color),
        interactions: [{ type: 'element-active' }],
    } as any;

    const chartData = (analyticsData?.revenueTrend || []).map((item: any) => ({
        ...item,
        revenueValue: item.revenue?.amount || 0
    }));

    const lineConfig = {
        data: chartData,
        xField: 'date',
        yField: 'revenueValue',
        colorField: 'period',
        shapeField: 'smooth',
        autoFit: true,
        scale: { color: { range: ['#1890ff', '#bfbfbf'] } },
        axis: {
            y: { title: false, labelFormatter: (v: string) => `${(Number(v) / 100000).toFixed(1)}L` },
            x: { title: false }
        },
        tooltip: { title: (d: any) => d.date, items: [{ field: 'revenueValue' }] },
        legend: { color: { position: 'bottom', layout: { justifyContent: 'center' } } },
        point: { shapeField: 'circle', sizeField: 4 },
    } as any;

    return (
        <Row gutter={[16, 16]}>
            <Col span={5}>
                <Card title="Revenue Breakdown" extra={<a href="#" onClick={(e) => { e.preventDefault(); navigate('/reports/revenue'); }}>View Report</a>} style={{ height: '100%' }} styles={{ body: { display: 'flex', flexDirection: 'column', alignItems: 'center' } }}>
                    <div style={{ width: '100%', height: 160 }}><Pie {...pieConfig} /></div>
                    <div style={{ width: '100%', marginTop: 16 }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {breakdown.map((item: any) => (
                                <div key={item.type} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Space>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color }} />
                                        <Text style={{ fontSize: 12 }}>{item.type}</Text>
                                    </Space>
                                    <Text strong style={{ fontSize: 12 }}>₹{formatMoney({ amount: item.value, currency: 'INR' })}</Text>
                                </div>
                            ))}
                        </Space>
                    </div>
                </Card>
            </Col>
            <Col span={9}>
                <Card
                    title="Revenue Trend"
                    extra={<Space><a href="#" onClick={(e) => { e.preventDefault(); navigate('/reports/revenue'); }}>View Report</a><Select value={trendTimeframe} onChange={setTrendTimeframe} size="small" options={[{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }, { value: 'yearly', label: 'Yearly' }]} /></Space>}
                    style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                    styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' } }}
                >
                    <div style={{ height: 230, width: '100%' }}><Line {...lineConfig} /></div>
                </Card>
            </Col>
            <Col span={5}>
                <Card title="Top Revenue Sources" style={{ height: '100%', display: 'flex', flexDirection: 'column' }} styles={{ body: { paddingTop: 20, flex: 1, display: 'flex', flexDirection: 'column' } }}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {breakdown.map((item: any) => {
                            const percent = Math.round((item.value / totalGross) * 100);
                            return (
                                <div key={item.type}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Space>
                                            <div style={{ backgroundColor: item.color + '20', padding: 4, borderRadius: 4, display: 'flex' }}>
                                                <WalletOutlined style={{ color: item.color, fontSize: 12 }} />
                                            </div>
                                            <Text style={{ fontSize: 12 }}>{item.type}</Text>
                                        </Space>
                                        <Space>
                                            <Text type="secondary" style={{ fontSize: 12 }}>{percent}%</Text>
                                            <Text strong style={{ fontSize: 12 }}>₹{(item.value / 10000000).toFixed(2)}L</Text>
                                        </Space>
                                    </div>
                                    <Progress percent={percent} showInfo={false} strokeColor={item.color} size="small" />
                                </div>
                            );
                        })}
                    </Space>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                        <Text strong>Total Gross</Text>
                        <Text strong>₹ {formatMoney(analyticsData?.grossRevenue)}</Text>
                    </div>
                </Card>
            </Col>
            <Col span={5}>
                <Card title="Sponsored Ads Overview" extra={<a href="#" onClick={(e) => { e.preventDefault(); navigate('/campaigns'); }}>View All</a>} style={{ height: '100%', display: 'flex', flexDirection: 'column' }} styles={{ body: { padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' } }}>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <div style={{ width: '100%', height: 120, backgroundColor: '#f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                            <img src="https://images.unsplash.com/photo-1541888087618-c5763b65e9ec?auto=format&fit=crop&w=600&q=80" alt="Ad" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 12 }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#6366f1' }} />
                            <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#d9d9d9' }} />
                            <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#d9d9d9' }} />
                            <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#d9d9d9' }} />
                        </div>
                        <div style={{ display: 'flex', flex: 1, alignItems: 'center', marginTop: 16 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 12px', width: '100%' }}>
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Active Ads</Text>
                                    <Text strong style={{ fontSize: 15 }}>{formatNumber(adsData?.snapshotMetrics?.activeAds)}</Text>
                                </Space>
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Total Sponsors</Text>
                                    <Text strong style={{ fontSize: 15 }}>{formatNumber(adsData?.snapshotMetrics?.totalSponsors)}</Text>
                                </Space>
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Impressions</Text>
                                    <Text strong style={{ fontSize: 15 }}>{formatNumber(adsData?.periodMetrics?.impressions)}</Text>
                                </Space>
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Clicks</Text>
                                    <Text strong style={{ fontSize: 15 }}>{formatNumber(adsData?.periodMetrics?.clicks)}</Text>
                                </Space>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: 16 }}>
                        <Text type="secondary" strong style={{ fontSize: 12 }}>Ad Revenue</Text>
                        <Space direction="vertical" align="end" size={0}>
                            <Text strong style={{ fontSize: 14 }}>₹ {formatMoney(adsData?.periodMetrics?.revenue)}</Text>
                        </Space>
                    </div>
                </Card>
            </Col>
        </Row>
    );
};
